/*
 * Hamilton Sinkler Custom Wishlist API
 * Description: Wishlist Prime V2 REST client using the documented myshopapps path schema, xtoken auth, timeout, retry, and normalized responses.
 * Dependencies: hs-wishlist-core.js
 * Public Methods: configure, fetchWishlistData, fetchWishlistCount, fetchStorefrontProduct, checkWishlist, addToWishlist, removeWishlist
 * Private Methods: normalizeBaseUrl, buildUrl, formBody, request, storefrontRequest, storefrontConfig, moneyToCents, normalizeItem, unwrapItems, unwrapCount
 * Version: 1.3.0
 */
(function (window) {
  'use strict';
  var HS = window.HSWishlist;
  var config = { baseUrl: 'https://api.myshopapps.com', xtoken: '', customerId: '', categoryId: 0, timeout: 15000, limit: 100 };
  var storefrontProducts = {};

  function normalizeBaseUrl() {
    return config.baseUrl.replace(/\/$/, '').replace(/\/wishlist\/V2$/i, '');
  }

  function buildUrl(path, query) {
    var url = normalizeBaseUrl() + path;
    var params = new URLSearchParams();
    Object.keys(query || {}).forEach(function (key) {
      if (query[key] !== undefined && query[key] !== null && query[key] !== '') params.append(key, query[key]);
    });
    return params.toString() ? url + '?' + params.toString() : url;
  }

  function formBody(data) {
    var body = new URLSearchParams();
    Object.keys(data || {}).forEach(function (key) {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') body.append(key, data[key]);
    });
    return body;
  }

  function moneyToCents(value) {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number') return value > 0 && value < 10000 && value % 1 !== 0 ? Math.round(value * 100) : Math.round(value);
    var clean = String(value).replace(/[^0-9.-]/g, '');
    if (!clean) return 0;
    return clean.indexOf('.') > -1 ? Math.round(Number(clean) * 100) : Math.round(Number(clean));
  }
  function normalizeTags(value) {
    if (Array.isArray(value)) return value.map(function (tag) { return String(tag || '').trim().toLowerCase(); }).filter(Boolean);
    return String(value || '').split(',').map(function (tag) { return tag.trim().toLowerCase(); }).filter(Boolean);
  }
  function normalizeItem(raw) {
    raw = raw || {};
    var product = raw.product || raw.product_data || raw;
    var variant = raw.variant || raw.variant_data || (product.variants && product.variants[0]) || raw;
    var variantId = raw.variant_id || raw.variantId || variant.id || '';
    var productId = raw.product_id || raw.productId || product.id || raw.id || '';
    var productImage = raw.featuredImage || raw.featured_image || product.featuredImage || product.featured_image || product.image || raw.product_image || '';
    var variantImage = variant.image || raw.variant_image || '';
    var variantTitle = variant.title || raw.variant_title || '';
    var tags = normalizeTags(product.tags || raw.tags || raw.product_tags || raw.productTags);
    
    return Object.freeze({
      id: String(variantId || raw.wishlist_id || productId), productId: String(productId), variantId: String(variantId),
      title: product.title || raw.product_title || raw.title || 'Product', handle: product.handle || raw.handle || '', url: product.url || raw.product_url || (product.handle || raw.handle ? '/products/' + (product.handle || raw.handle) : '#'),
      image: variantImage || productImage, sku: variant.sku || raw.sku || '', category: product.product_type || product.type || raw.category || raw.product_type || '', finish: raw.finish || variant.option1 || (variantTitle.split('/')[0] || '').trim(), size: raw.size || variant.option2 || (variantTitle.split('/')[1] || '').trim(),
      quantity: Number(raw.product_qty || raw.quantity || 1), retailPrice: moneyToCents(variant.price || raw.price || raw.retail_price), tradePrice: moneyToCents(variant.price || raw.price || raw.retail_price),
      available: raw.available !== false && variant.available !== false && variant.availableForSale !== false, tags: tags, noPrice: tags.indexOf('no_price') > -1, tearSheetUrl: raw.tear_sheet_url || raw.tearSheetUrl || raw.tear_sheet || '', addedAt: raw.created_at || raw.added_at || ''
   });
  }

  function unwrapItems(data) {
    var items = data && (data.result || data.items || data.data || data);
    return { items: Array.isArray(items) ? items.map(normalizeItem) : [], totalRecords: Number(data && (data.total_records || data.totalRecords || 0)) || (Array.isArray(items) ? items.length : 0), currentPage: Number(data && (data.current_page || data.currentPage || 1)) || 1, nextPage: data ? data.next_page : null, prevPage: data ? data.prev_page : null };
  }

  function unwrapCount(data) {
    return Number(data && (data.result || data.count || data.data || data.total_records || 0)) || 0;
  }

  function request(path, options, retry) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, config.timeout);
    var init = Object.assign({ method: 'GET', headers: { Accept: 'application/json', xtoken: config.xtoken }, signal: controller.signal }, options || {});
    return fetch(path, init).then(function (response) {
      clearTimeout(timer);
      if (!response.ok) throw new Error('Wishlist API error: ' + response.status + ' for ' + path);
      return response.json();
    }).catch(function (error) {
      clearTimeout(timer);
      if (retry !== false) return request(path, options, false);
      HS.emit('wishlist:error', { message: error.message });
      throw error;
    });
  }

  function storefrontConfig() {
    var bmt = window.bmtConfig || {}, shopDomain = String(bmt.shopDomain || (window.Shopify && window.Shopify.shop) || window.location.hostname).replace(/^https?:\/\//i, '').replace(/\/$/, ''), marketHandle = String(bmt.market && bmt.market.handle || '').toLowerCase();
    return { token: bmt.storefrontAccessToken || '', shopDomain: shopDomain, country: bmt.countryCode || (!marketHandle || marketHandle === 'international' ? 'US' : marketHandle.toUpperCase()) };
  }

  function storefrontRequest(path, options, retry) {
    var controller = new AbortController(), timer = setTimeout(function () { controller.abort(); }, config.timeout);
    return fetch(path, Object.assign({ signal: controller.signal }, options || {})).then(function (response) {
      clearTimeout(timer);
      if (!response.ok) throw new Error('Storefront API error: ' + response.status);
      return response.json();
    }).catch(function (error) {
      clearTimeout(timer);
      if (retry !== false) return storefrontRequest(path, options, false);
      throw error;
    });
  }
  /** Configures the Wishlist Prime V2 API client. */
  function configure(nextConfig) { config = Object.assign({}, config, nextConfig || {}); }

  /** Fetches normalized wishlist item data and pagination metadata for the configured customer. */
  function fetchWishlistData(page, limit) { return request(buildUrl('/wishlist/V2/fetchWishlistData/' + encodeURIComponent(config.customerId), { category_id: config.categoryId, page: page || 1, limit: limit || config.limit })).then(unwrapItems); }

  /** Fetches the Wishlist Prime wishlist count for the configured customer. */
  function fetchWishlistCount() { return request(buildUrl('/wishlist/V2/count/' + encodeURIComponent(config.customerId))).then(unwrapCount); }

  /** Fetches and caches Storefront API product data required for BMT collection-aware rule matching. */
  function fetchStorefrontProduct(handle) {
    var nextHandle = String(handle || '').trim(), bmtProduct = window.bmtProducts && window.bmtProducts[nextHandle], settings = storefrontConfig();
    if (!nextHandle) return Promise.resolve(null);
    if (bmtProduct) return Promise.resolve(bmtProduct);
    if (storefrontProducts[nextHandle]) return Promise.resolve(storefrontProducts[nextHandle]);
    if (!settings.token || !settings.shopDomain) return Promise.resolve(null);
    var query = 'query WishlistBmtProduct($handle: String!, $country: CountryCode) @inContext(country: $country) { product(handle: $handle) { id handle variants(first: 250) { edges { node { id price { amount currencyCode } } } } collections(first: 250) { edges { node { id handle title } } } } }';
    return storefrontRequest('https://' + settings.shopDomain + '/api/2025-01/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': settings.token },
      body: JSON.stringify({ query: query, variables: { handle: nextHandle, country: settings.country } })
    }).then(function (data) {
      var product = data && data.data && data.data.product;
      if (!product) return null;
      storefrontProducts[nextHandle] = product;
      return product;
    }).catch(function () { return null; });
  }
  /** Checks whether a variant is in the configured customer's wishlist. */
  function checkWishlist(variantId) { return request(buildUrl('/wishlist/V2/checkInWishlist', { customer_id: config.customerId, variant_id: variantId })).then(function (data) { return Boolean(data && data.result); }); }

  /** Adds a product variant to the configured customer's wishlist. */
  function addToWishlist(productId, variantId, quantity) { return request(buildUrl('/wishlist/V2/addToWishlist'), { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded', xtoken: config.xtoken }, body: formBody({ customer_id: config.customerId, product_id: productId, variant_id: variantId, category_id: config.categoryId, product_qty: quantity || 1 }) }).then(function () { return checkWishlist(variantId); }); }

  /** Removes a variant from the configured customer's wishlist. */
  function removeWishlist(idOrItem) { var variantId = typeof idOrItem === 'object' ? idOrItem.variantId : idOrItem; return request(buildUrl('/wishlist/V2/removeWishlist', { customer_id: config.customerId, variant_id: variantId, category_id: config.categoryId }), { method: 'DELETE' }).then(function () { return String(variantId); }); }

  HS.define('api', { configure: configure, fetchWishlistData: fetchWishlistData, fetchWishlistCount: fetchWishlistCount, fetchStorefrontProduct: fetchStorefrontProduct, checkWishlist: checkWishlist, addToWishlist: addToWishlist, removeWishlist: removeWishlist });
})(window);
